import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Button, Col, Row, Table, Tag, message } from 'antd';
import PageLoader from '../../../../../../components/PageLoader/PageLoader';
import ModuleEmptyState from '../../../../../../components/ModuleEmptyState';
import Card from '../../../../../../components/Card/Card';
import StartSubmissionModal from '../StartSubmissionModal/StartSubmissionModal';
import MockBanner from '../MockBanner/MockBanner';
import {
  fetchScenarios,
  fetchScenarioSubmissions,
  createScenarioSubmission,
} from '../../TestingExercisingAction';
import {
  scenariosSelector,
  industryNameSelector,
  loadingScenariosSelector,
  scenarioSubmissionsSelector,
  loadingSubmissionsSelector,
  submissionRequestSelector,
  usingMockSelector,
} from '../../TestingExercisingSelectors';
import { planIdSelector, planExpiryDateSelector } from '../../../Plan/PlanSelectors';
import { getPlanExpiryStatus } from '../../../../../../components/PlanExpiryBadge/plan-expiry-shared';
import { MSG_ERROR } from '../../../../../../constants';
import { getApiErrorMessage } from '../../../../../../utils/httpError';
import dayjs from '../../../../../../utils/dayjs';

function statusColor(status, accentColor) {
  const value = String(status || '').toLowerCase();
  if (value.includes('submit') || value === 'completed') return 'green';
  if (value.includes('cancel')) return 'red';
  if (value.includes('draft') || value.includes('progress')) return accentColor || 'blue';
  return 'default';
}

const EXPIRED_PLAN_REASON = 'This plan has expired. Renew the subscription to start a walkthrough.';

function resolveVideoCount(scenario) {
  if (Number.isFinite(Number(scenario?.videoCount)) && Number(scenario.videoCount) >= 0) {
    return Number(scenario.videoCount);
  }
  if (Array.isArray(scenario?.videos)) return scenario.videos.length;
  return 0;
}

function ScenarioHub({
  history,
  planId,
  planExpiry,
  scenarios,
  industryName,
  loadingScenarios,
  submissions,
  loadingSubmissions,
  submissionRequest,
  usingMock,
  fetchScenarios,
  fetchScenarioSubmissions,
  createScenarioSubmission,
  accentColor,
}) {
  const [selectedScenarioId, setSelectedScenarioId] = useState(null);

  useEffect(() => {
    if (!planId) return;
    fetchScenarios(planId).catch((err) => message.error(getApiErrorMessage(err, MSG_ERROR)));
  }, [planId, fetchScenarios]);

  useEffect(() => {
    if (!scenarios.length) return;
    setSelectedScenarioId((prev) => prev || scenarios[0].scenarioId);
  }, [scenarios]);

  useEffect(() => {
    if (!planId || selectedScenarioId == null) return;
    fetchScenarioSubmissions(planId, selectedScenarioId).catch((err) =>
      message.error(getApiErrorMessage(err, MSG_ERROR)),
    );
  }, [planId, selectedScenarioId, fetchScenarioSubmissions]);

  const isPlanExpired = getPlanExpiryStatus(planExpiry) === 'expired';
  const selectedScenario = scenarios.find((item) => item.scenarioId === selectedScenarioId);
  const sortedScenarios = scenarios.slice().sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
  const selectedVideoCount = selectedScenario ? resolveVideoCount(selectedScenario) : 0;

  const onStart = async ({ participant, questionnaireDate }) => {
    if (isPlanExpired) return;
    const created = await createScenarioSubmission(planId, {
      scenarioId: selectedScenarioId,
      participant,
      questionnaireDate,
    });
    const submissionId = created?.submissionId;
    if (!submissionId) throw new Error('Missing submissionId');
    history.push(`/dashboard/testing-exercising/scenarios/${selectedScenarioId}/submissions/${submissionId}`);
  };

  const columns = [
    {
      title: 'Participant',
      dataIndex: 'participant',
      key: 'participant',
    },
    {
      title: 'Exercise date',
      dataIndex: 'questionnaireDate',
      key: 'questionnaireDate',
      render: (value) => (value ? dayjs(value).format('DD MMM YYYY') : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (value) => <Tag color={statusColor(value, accentColor)}>{value || '—'}</Tag>,
    },
    {
      title: 'Submitted',
      dataIndex: 'submittedDate',
      key: 'submittedDate',
      render: (value) => (value ? dayjs(value).format('DD MMM YYYY HH:mm') : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      render: (_, record) => {
        const value = String(record.status || '').toLowerCase();
        const isClosed = value.includes('submit') || value.includes('cancel') || value === 'completed';
        return (
          <Button
            type="link"
            onClick={() =>
              history.push(
                `/dashboard/testing-exercising/scenarios/${selectedScenarioId}/submissions/${record.submissionId}`,
              )
            }
          >
            {isClosed ? 'View' : 'Open'}
          </Button>
        );
      },
    },
  ];

  if (loadingScenarios && !scenarios.length) return <PageLoader tip="Loading scenarios..." />;

  if (!scenarios.length) {
    return (
      <ModuleEmptyState
        embedded
        moduleTitle="Testing & Exercising"
        headline="No scenarios yet"
        description="Scenario walkthroughs for this plan will appear here once they are configured for your industry."
      />
    );
  }

  return (
    <div className="testing-exercising-hub">
      {usingMock ? (
        <MockBanner>
          Demo data is active so you can build UI/UX while scenario content is incomplete. Mutations (save / submit)
          stay local.
        </MockBanner>
      ) : null}

      <Card
        color={accentColor}
        title="Scenario walkthroughs"
        description={
          industryName
            ? `Choose a scenario for ${industryName}, then start a facilitated walkthrough with your team.`
            : 'Choose a scenario, then start a facilitated walkthrough with your team.'
        }
      >
        <div className="testing-exercising-hub__inner">
          <section className="testing-exercising-hub__chooser" aria-label="Scenario selection">
            <div className="testing-exercising-hub__section-label">Choose a scenario</div>
            <Row gutter={[12, 12]} className="testing-exercising-scenario-grid" type="flex">
              {sortedScenarios.map((scenario, index) => {
                const isSelected = scenario.scenarioId === selectedScenarioId;
                const videoCount = resolveVideoCount(scenario);
                return (
                  <Col key={scenario.scenarioId} xs={24} sm={12} md={8} lg={6}>
                    <button
                      type="button"
                      className={`testing-exercising-scenario-tile${isSelected ? ' is-selected' : ''}`}
                      onClick={() => setSelectedScenarioId(scenario.scenarioId)}
                      aria-pressed={isSelected}
                    >
                      <div className="testing-exercising-scenario-tile__top">
                        <span className="testing-exercising-scenario-tile__num">{index + 1}</span>
                        {isSelected ? (
                          <span className="testing-exercising-scenario-tile__selected">Selected</span>
                        ) : null}
                      </div>
                      <h3 className="testing-exercising-scenario-tile__title">{scenario.heading}</h3>
                      <p className="testing-exercising-scenario-tile__desc">
                        {scenario.description || 'Facilitated scenario walkthrough for plan validation.'}
                      </p>
                      <div className="testing-exercising-scenario-tile__meta">
                        <span>
                          {videoCount} video{videoCount === 1 ? '' : 's'}
                        </span>
                        {scenario.pdf?.downloadUrl && scenario.pdf.downloadUrl !== '#' ? (
                          <span>Handout available</span>
                        ) : null}
                      </div>
                    </button>
                  </Col>
                );
              })}
            </Row>
          </section>

          {selectedScenario ? (
            <section className="testing-exercising-hub__detail" aria-label="Selected scenario">
              <div className="testing-exercising-hub__detail-head">
                <div className="testing-exercising-hub__detail-copy">
                  <div className="testing-exercising-hub__section-label">Selected scenario</div>
                  <h3>{selectedScenario.heading}</h3>
                  <p>
                    {selectedScenario.description ||
                      'Work through each video with your team and document your responses.'}
                  </p>
                  <div className="testing-exercising-hub__detail-meta">
                    <span>
                      {selectedVideoCount} video{selectedVideoCount === 1 ? '' : 's'}
                    </span>
                    {industryName ? <span>{industryName}</span> : null}
                    <span>
                      {submissions.length} submission{submissions.length === 1 ? '' : 's'}
                    </span>
                  </div>
                </div>
                <div className="testing-exercising-hub__detail-actions">
                  {selectedScenario.pdf?.downloadUrl && selectedScenario.pdf.downloadUrl !== '#' ? (
                    <Button
                      icon="download"
                      href={selectedScenario.pdf.downloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Download handout
                    </Button>
                  ) : null}
                  <StartSubmissionModal
                    loading={submissionRequest}
                    onStart={onStart}
                    disabled={isPlanExpired}
                    disabledReason={EXPIRED_PLAN_REASON}
                  />
                </div>
              </div>

              <div className="testing-exercising-hub__submissions">
                <div className="testing-exercising-hub__submissions-head">
                  <h4 className="testing-exercising-hub__submissions-title">Previous submissions</h4>
                  <span className="testing-exercising-hub__submissions-count">
                    {loadingSubmissions ? 'Loading…' : `${submissions.length} total`}
                  </span>
                </div>
                <Table
                  rowKey="submissionId"
                  size="middle"
                  loading={loadingSubmissions}
                  columns={columns}
                  dataSource={submissions}
                  pagination={submissions.length > 5 ? { pageSize: 5 } : false}
                  locale={{ emptyText: 'No submissions yet — start a walkthrough to begin.' }}
                />
              </div>
            </section>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

const mapStateToProps = (state) => ({
  planId: planIdSelector(state),
  planExpiry: planExpiryDateSelector(state),
  scenarios: scenariosSelector(state),
  industryName: industryNameSelector(state),
  loadingScenarios: loadingScenariosSelector(state),
  submissions: scenarioSubmissionsSelector(state),
  loadingSubmissions: loadingSubmissionsSelector(state),
  submissionRequest: submissionRequestSelector(state),
  usingMock: usingMockSelector(state),
});

const mapDispatchToProps = {
  fetchScenarios,
  fetchScenarioSubmissions,
  createScenarioSubmission,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(ScenarioHub));
